import { ParcelEndpointInfo } from '@lanterds/types/build/parcelSearch';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { DateFormats, ShippingPortalLocation } from '@lanterds/types';

dayjs.extend(utc);

export const formatEndpointAddress = (
    locationInfo: ShippingPortalLocation | ParcelEndpointInfo | undefined,
    includeEndpointNumber: boolean,
    useParcelEndpointInfo = true,
    includeEndpointName = true,
) => {
    let formattedAddress = '';

    if (locationInfo) {
        let { locationCode, name, city, state, zip, street } = locationInfo as ShippingPortalLocation;
        if (useParcelEndpointInfo) {
            const { endPointNumber, endPointName, endPointCity, endPointState, endPointZip, endPointStreet } =
                locationInfo as ParcelEndpointInfo;
            locationCode = endPointNumber;
            name = endPointName;
            city = endPointCity;
            state = endPointState;
            zip = endPointZip;
            street = endPointStreet;
        }
        const parts = [];

        if (includeEndpointNumber && locationCode && locationCode.toUpperCase() !== 'NULL') parts.push(locationCode);
        if (includeEndpointName && name && name.toUpperCase() !== 'NULL') parts.push(name);
        if (street && street.toUpperCase() !== 'NULL') parts.push(street);
        if (city && city.toUpperCase() !== 'NULL') parts.push(city);
        if (state && state.toUpperCase() !== 'NULL') parts.push(state);
        if (zip && zip.toUpperCase() !== 'NULL') parts.push(zip);

        formattedAddress = parts.join(', ');
    }

    if (formattedAddress.length < 1) return 'N/A';

    return formattedAddress.toUpperCase();
};

export const formatEndpointAddressHtml = (locationInfo: ShippingPortalLocation | undefined, includeLocationCode: boolean) => {
    if (locationInfo) {
        const { locationCode, name, city, state, zip } = locationInfo;

        const hasLocationCode = includeLocationCode && locationCode && locationCode.toUpperCase() !== 'NULL';
        const hasEndpointName = name && name.toUpperCase() !== 'Null';
        const hasEndpointCity = city && city.toUpperCase() !== 'NULL';
        const hasEndpointState = state && state.toUpperCase() !== 'NULL';
        const hasEndpointZip = zip && zip.toUpperCase() !== 'NULL';

        return (
            <div>
                {(hasLocationCode || hasEndpointName) && (
                    <div>
                        {hasLocationCode && <span>{locationCode.toUpperCase()} </span>}
                        {hasEndpointName && <span>{name.toUpperCase()}</span>}
                    </div>
                )}
                {(hasEndpointCity || hasEndpointState || hasEndpointZip) && (
                    <div>
                        {hasEndpointCity && <span>{city.toUpperCase()}, </span>}
                        {hasEndpointState && <span>{state.toUpperCase()} </span>}
                        {hasEndpointZip && <span>{zip.toUpperCase()}</span>}
                    </div>
                )}
            </div>
        );
    }
};

export const formatDate = (date: Date | string | undefined | null, format: DateFormats = DateFormats.LongDateTime) => {
    /**
     * The API returns all times coded as UTC.
     *
     * However! What we are ACTUALLY returning from the API is the time that we WANT to be displayed. Sometimes that is UTC, sometimes that is the timezone that the time occurred in.
     *
     * What we DON'T want is for the browser to convert any of the times to local browser time. So we return all times as UTC and display as UTC.
     */
    return date ? dayjs(date).utc().format(format) : null;
};

export const formatAndHandleUtcFallback = (
    dateLocal: Date | string | undefined | null,
    dateUtc: Date | string | undefined | null,
    fallbackValue = '',
) => {
    if (dateLocal) {
        return formatDate(dateLocal);
    } else if (dateUtc) {
        return `${formatDate(dateUtc)} (UTC)`;
    } else {
        return fallbackValue;
    }
};
